/*
 * FESK Fleet Management design tokens.
 *
 * The look borrows from the things already in a truck workshop: warm manila
 * paper, hi-vis amber, black-on-yellow number plates, and the flat steel of a
 * roller shutter. Everything is sized for a thumb on a phone screen in a yard.
 *
 * Depth comes from tonal layering, not borders. Four surface levels are
 * defined per theme; cards sit on --surface-1 with no outline, and borders are
 * reserved for inputs and hairline dividers. In dark mode the levels are
 * tinted toward the petrol brand so black reads as deep rather than dead.
 */

:root {
  color-scheme: light;

  /* ---- Surfaces ----------------------------------------------------- */
  --surface-0: #f2efe7; /* page: warm paper, slightly deeper so cards lift */
  --surface-1: #fffdf9; /* card */
  --surface-2: #ffffff; /* raised: sheets, popovers, active controls */
  --surface-3: #ffffff; /* highest: menus */
  --surface-sunk: #e8e3d8; /* wells, track backgrounds */
  /* Switch knobs stay light in both themes, the way a physical toggle does:
     a dark knob on a dark track reads as a hole, not a control. */
  --knob: #ffffff;

  --line: #e4dfd3; /* hairline dividers */
  --line-strong: #c6bfaf; /* input borders */

  /* Kept as aliases so nothing needs to change in one go. */
  --paper: var(--surface-0);
  --surface: var(--surface-1);

  /* ---- Ink ---------------------------------------------------------- */
  --ink: #15191a;
  --ink-2: #454c4e;
  --ink-3: #5d6467;
  --ink-inverse: #fbf9f5;

  /* ---- Brand -------------------------------------------------------- */
  --brand: #0f3f3a;
  --brand-2: #1b5c55;
  --brand-3: #2a7a70; /* lighter, for glows and highlights */
  --brand-tint: #dfe9e5;
  --brand-ink: #f2f7f5;

  --hazard: #e8a013;
  --hazard-deep: #a9700a;
  --hazard-tint: #fbeecd;

  /* ---- Status tones ------------------------------------------------- */
  /* Text colours. Fills exist only for the two states that must interrupt. */
  --danger: #b3261e;
  --danger-tint: #f8dedb;
  --danger-ink: #7c1a15;

  --warn: #8a5a06;
  --warn-tint: #fbeecd;

  --ok: #1c6b47;
  --ok-tint: #d9ece2;

  --info: #1f4f78;
  --info-tint: #dde8f2;

  /* Number plate yellow, for the plate component only. */
  --plate: #f5d312;
  --plate-ink: #101010;
  --plate-band: #0b3a9e;

  /* ---- Type --------------------------------------------------------- */
  --font-display: 'Archivo Variable', 'Archivo', 'Helvetica Neue', Arial, sans-serif;
  --font-body: 'Instrument Sans Variable', 'Instrument Sans', 'Helvetica Neue', Arial, sans-serif;
  --font-mono: 'JetBrains Mono Variable', 'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace;
  /* Number plates only. A real UK plate uses Charles Wright, whose characters
     are markedly taller than they are wide; Archivo's grotesque caps are far
     too wide for it, and squashing them would thin the strokes unevenly. */
  --font-plate: 'Archivo Narrow Variable', 'Archivo Narrow', 'Arial Narrow', var(--font-display);

  /* Six steps. Every piece of text in the app uses one of these. */
  --text-display: 1.75rem;
  --lh-display: 1.08;
  --text-title: 1.25rem;
  --lh-title: 1.2;
  --text-heading: 1.0625rem;
  --lh-heading: 1.25;
  --text-body: 0.9375rem;
  --lh-body: 1.5;
  --text-small: 0.8125rem;
  --lh-small: 1.45;
  --text-micro: 0.6875rem;
  --lh-micro: 1.3;

  /* ---- Space -------------------------------------------------------- */
  --s1: 0.25rem;
  --s2: 0.5rem;
  --s3: 0.75rem;
  --s4: 1rem;
  --s5: 1.5rem;
  --s6: 2rem;
  --s7: 3rem;

  --radius-sm: 8px;
  --radius: 12px;
  --radius-lg: 18px;

  /* ---- Elevation ---------------------------------------------------- */
  /* Soft, warm-tinted shadows: the paper is not pure white, so neither is
     the shadow. */
  --shadow-1: 0 1px 2px rgb(38 31 18 / 0.06), 0 1px 1px rgb(38 31 18 / 0.04);
  --shadow-2: 0 4px 12px rgb(38 31 18 / 0.08), 0 1px 3px rgb(38 31 18 / 0.06);
  --shadow-3: 0 16px 40px rgb(38 31 18 / 0.16), 0 4px 12px rgb(38 31 18 / 0.08);
  /* The primary action is the one object on the screen that casts a
     coloured shadow. */
  --shadow-brand: 0 6px 18px rgb(15 63 58 / 0.28), 0 1px 3px rgb(15 63 58 / 0.2);
  /* Inner top highlight on raised surfaces; invisible in light, a light
     source in dark. */
  --highlight: inset 0 1px 0 rgb(255 255 255 / 0);

  /* ---- Motion ------------------------------------------------------- */
  --dur-fast: 120ms;
  --dur-base: 220ms;
  --dur-slow: 360ms;
  --ease-out: cubic-bezier(0.22, 1, 0.36, 1);
  --ease-in-out: cubic-bezier(0.65, 0, 0.35, 1);
  --ease-spring: cubic-bezier(0.34, 1.4, 0.64, 1);

  /* Minimum comfortable target for a gloved thumb. */
  --tap: 44px;

  --header-h: 3.5rem;
  --tabbar-h: 4rem;
  --maxw: 60rem;
}

/* ---------------------------------------------------------------------- *
 * Dark
 *
 * Declared twice: once for the system preference, once for an explicit
 * choice, so the toggle wins in both directions. Values are identical.
 * ---------------------------------------------------------------------- */

@media (prefers-color-scheme: dark) {
  :root:not([data-theme='light']) {
    color-scheme: dark;

    --surface-0: #0b1011;
    --surface-1: #141b1d;
    --surface-2: #1c2528;
    --surface-3: #253033;
    --surface-sunk: #070b0c;
    --knob: #d9dfe0;

    --line: #222d30;
    --line-strong: #354347;

    --ink: #edf0ef;
    --ink-2: #b4bcbe;
    --ink-3: #939ca0;
    --ink-inverse: #0b1011;

    --brand: #3f9c8e;
    --brand-2: #56b6a6;
    --brand-3: #7fd0c2;
    --brand-tint: #16302c;
    --brand-ink: #06110f;

    --hazard: #f0ad2b;
    --hazard-deep: #f0ad2b;
    --hazard-tint: #33280f;

    --danger: #ef7a70;
    --danger-tint: #3a1c1c;
    --danger-ink: #f6aaa3;

    --warn: #e6ad3e;
    --warn-tint: #33280f;

    --ok: #5cc094;
    --ok-tint: #13291f;

    --info: #79b0dc;
    --info-tint: #17252f;

    --plate-band: #1a4fc4;

    --shadow-1: 0 1px 2px rgb(0 0 0 / 0.5);
    --shadow-2: 0 6px 16px rgb(0 0 0 / 0.5), 0 1px 3px rgb(0 0 0 / 0.4);
    --shadow-3: 0 20px 48px rgb(0 0 0 / 0.65), 0 4px 12px rgb(0 0 0 / 0.5);
    --shadow-brand: 0 0 0 1px rgb(63 156 142 / 0.35), 0 8px 24px rgb(63 156 142 / 0.28);
    --highlight: inset 0 1px 0 rgb(255 255 255 / 0.05);
  }
}

:root[data-theme='dark'] {
  color-scheme: dark;

  --surface-0: #0b1011;
  --surface-1: #141b1d;
  --surface-2: #1c2528;
  --surface-3: #253033;
  --surface-sunk: #070b0c;
  --knob: #d9dfe0;

  --line: #222d30;
  --line-strong: #354347;

  --ink: #edf0ef;
  --ink-2: #b4bcbe;
  --ink-3: #939ca0;
  --ink-inverse: #0b1011;

  --brand: #3f9c8e;
  --brand-2: #56b6a6;
  --brand-3: #7fd0c2;
  --brand-tint: #16302c;
  --brand-ink: #06110f;

  --hazard: #f0ad2b;
  --hazard-deep: #f0ad2b;
  --hazard-tint: #33280f;

  --danger: #ef7a70;
  --danger-tint: #3a1c1c;
  --danger-ink: #f6aaa3;

  --warn: #e6ad3e;
  --warn-tint: #33280f;

  --ok: #5cc094;
  --ok-tint: #13291f;

  --info: #79b0dc;
  --info-tint: #17252f;

  --plate-band: #1a4fc4;

  --shadow-1: 0 1px 2px rgb(0 0 0 / 0.5);
  --shadow-2: 0 6px 16px rgb(0 0 0 / 0.5), 0 1px 3px rgb(0 0 0 / 0.4);
  --shadow-3: 0 20px 48px rgb(0 0 0 / 0.65), 0 4px 12px rgb(0 0 0 / 0.5);
  --shadow-brand: 0 0 0 1px rgb(63 156 142 / 0.35), 0 8px 24px rgb(63 156 142 / 0.28);
  --highlight: inset 0 1px 0 rgb(255 255 255 / 0.05);
}
